import type { UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type {
  CreateSsoProviderData,
  DeleteSsoProviderData,
  UpdateSsoProviderData,
} from '@repositories/tenantConfigRepository/tenantConfig.types';
import type ITenantSsoProviderRepository from '@repositories/tenantSsoProviderRepository/ITenantSsoProviderRepository';
import { TenantSsoProviderModel } from '@repositories/tenantSsoProviderRepository/models/TenantSsoProviderModel';
import type {
  TenantSsoProviderDTO,
  TenantSsoProviderPublicDTO,
  TenantSsoProviderLookupDTO,
  AuthDiscoverRequestDTO,
  AuthDiscoverResultDTO,
} from '@repositories/tenantSsoProviderRepository/tenantSsoProvider.types';

const ssoProviderRepo = repository.get<ITenantSsoProviderRepository>(KEYS.TENANT_SSO_PROVIDER_REPOSITORY);

const ssoProviderKey = {
  all: ['ssoProvider'] as const,
  lists: () => [...ssoProviderKey.all, 'lists'] as const,
  list: (tenantId: string) => [...ssoProviderKey.lists(), tenantId] as const,
  details: () => [...ssoProviderKey.all, 'detail'] as const,
  detail: (tenantId: string, providerId: string) => [...ssoProviderKey.details(), tenantId, providerId] as const,
};

// SSO 프로바이더 목록 조회
export const useGetSsoProviders = (
  tenantId: string,
  options?: Omit<
    UseQueryOptions<TenantSsoProviderDTO[], AxiosError, TenantSsoProviderModel[]>,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  const selectSsoProviderList = useCallback(
    (data: TenantSsoProviderDTO[]) => data.map(dto => new TenantSsoProviderModel(dto)),
    [],
  );

  return useQuery<TenantSsoProviderDTO[], AxiosError, TenantSsoProviderModel[]>({
    queryKey: ssoProviderKey.list(tenantId),
    queryFn: () => ssoProviderRepo.getSsoProviders(tenantId),
    select: selectSsoProviderList,
    ...options,
    enabled: !!tenantId && (options?.enabled ?? true),
  });
};

// SSO 프로바이더 상세 조회
export const useGetSsoProvider = (
  tenantId: string,
  providerId: string,
  options?: Omit<
    UseQueryOptions<TenantSsoProviderDTO, AxiosError, TenantSsoProviderModel>,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  const selectSsoProvider = useCallback((data: TenantSsoProviderDTO) => new TenantSsoProviderModel(data), []);

  return useQuery<TenantSsoProviderDTO, AxiosError, TenantSsoProviderModel>({
    queryKey: ssoProviderKey.detail(tenantId, providerId),
    queryFn: () => ssoProviderRepo.getSsoProvider(tenantId, providerId),
    select: selectSsoProvider,
    ...options,
    enabled: !!tenantId && !!providerId && (options?.enabled ?? true),
  });
};

// SSO 프로바이더 생성
export const useCreateSsoProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSsoProviderData) => {
      const result = await ssoProviderRepo.createSsoProvider(data.tenantId, data.dto);
      return new TenantSsoProviderModel(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKey.lists() });
    },
  });
};

// SSO 프로바이더 수정
export const useUpdateSsoProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateSsoProviderData) => {
      const result = await ssoProviderRepo.updateSsoProvider(data.tenantId, data.providerId, data.dto);
      return new TenantSsoProviderModel(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKey.all });
    },
  });
};

// SSO 프로바이더 삭제
export const useDeleteSsoProvider = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DeleteSsoProviderData) => {
      await ssoProviderRepo.deleteSsoProvider(data.tenantId, data.providerId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ssoProviderKey.all });
    },
  });
};

// SSO 프로바이더 lookup (로그인 페이지에서 사용)
export const useLookupSsoProviders = () => {
  return useMutation<TenantSsoProviderPublicDTO[], AxiosError, TenantSsoProviderLookupDTO>({
    mutationFn: (dto: TenantSsoProviderLookupDTO) => ssoProviderRepo.lookupSsoProviders(dto),
  });
};

// 인증 방식 탐색 (auth/discover - Email-First 로그인에서 사용)
export const useDiscoverLoginMethod = () => {
  return useMutation<AuthDiscoverResultDTO, AxiosError, AuthDiscoverRequestDTO>({
    mutationFn: (dto: AuthDiscoverRequestDTO) => ssoProviderRepo.discoverLoginMethod(dto),
  });
};
